// Strona pojedynczego artykułu z bazy wiedzy

import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { reduxActions, useDispatch } from 'store';
import { useGetKnowledgeBaseArticle } from 'api';
import { Container, Breadcrumbs, Loader } from 'components/controls';
import { CalendarIcon } from 'assets/icons';
import { generateKnowledgeBaseArticlePageScheme } from 'api/helpers';

import styles from 'theme/pages/KnowledgeBaseArticle/KnowledgeBaseArticle.module.scss';

const KnowledgeBaseArticle = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  const { data: article, isLoading } = useGetKnowledgeBaseArticle(id || '');

  const articlePageScheme = useMemo(() => {
    if (!article) return null;
    return generateKnowledgeBaseArticlePageScheme(article as any, window.location.href);
  }, [article]);

  useEffect(() => {
    if (article) {
      dispatch(
        reduxActions.setBreadcrumbs([
          { name: t('Baza wiedzy'), path: '/knowledge-base' },
          { name: article.title || '', path: undefined }
        ])
      );
    }
  }, [article]);

  if (isLoading) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
        <Loader />
      </div>
    );
  }

  if (!article) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
        <Container>
          <div>{t('Artykuł nie został znaleziony')}</div>
        </Container>
      </div>
    );
  }

  const fields = article.article_fields || {};
  const image = fields['image'] as string | null;
  const subtitle = fields['podtytuł'] || fields['subtitle'] || null;
  const content = fields['content'] || null;
  const date = article.sys_insert_date
    ? new Date(article.sys_insert_date).toLocaleDateString('pl-PL')
    : '';

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-KnowledgeBaseArticle')}>
      {article && (
        <Helmet>
          <title>{article.title}</title>
          <link rel="canonical" href={window.location.href} />
          {articlePageScheme &&
            (articlePageScheme as any[]).map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))}
        </Helmet>
      )}
      <Breadcrumbs />

      <Container>
        <div className={styles.articleContent}>
          {image && (
            <div className={styles.imageWrapper}>
              <img className={styles.image} src={image} alt={article.title} />
            </div>
          )}

          <h1 className={styles.title}>{article.title}</h1>

          <div className={styles.lineSeparator} />

          {date && (
            <div className={styles.meta}>
              <span className={styles.date}>
                <CalendarIcon className={styles.calendarIcon} />
                {date}
              </span>
            </div>
          )}

          {subtitle && (
            <h2 className={styles.subtitle} dangerouslySetInnerHTML={{ __html: subtitle as string }} />
          )}

          <article className={styles.article}>
            <div className={styles.contentWrapper}>
              {content && (
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{ __html: content as string }}
                />
              )}
            </div>
          </article>
        </div>
      </Container>
    </div>
  );
};

export default KnowledgeBaseArticle;
